// history.js — Activity history logging and rendering
// S3 neon port 2026-07-15 (History Mockup v0.1): slim hero (today count + type mix),
// chip type-filter driving the hidden #logTF input (same 6 values as the old <select>),
// day separators, color-coded action badges. lg() and the filter semantics are UNCHANGED.

import { S } from '../core/state.js';
import { now, ds, esc } from '../core/utils.js';

// order matters for includes()-matching: 'cash' must precede broader terms so 'cashflow' maps to cash
const TYPES = ['trade', 'alert', 'holding', 'cash', 'price', 'portfolio', 'advisory', 'system', 'sync', 'dividend'];
const _bdgType = a => TYPES.find(t => (a || '').includes(t)) || '';

// === LOGGING TO HISTORY ===
/**
 * Log an action to S.history
 * @param {string} a - Action type (e.g., 'trade', 'cash', 'price')
 * @param {string} t - Ticker/identifier
 * @param {string} d - Details/description
 */
export function lg(a, t, d) {
  S.history.unshift({ ts: now(), action: a, ticker: t, details: d });
  if (S.history.length > 1000) S.history.pop();
}

/** Chip click → set hidden #logTF (kept for filter-semantics parity) + toggle .on + re-render */
export function setLogTF(v) {
  const tf = document.getElementById('logTF');
  if (tf) tf.value = v;
  document.querySelectorAll('#histNeo .hist-chip').forEach(c => {
    let arg = 'all';
    try { arg = JSON.parse(c.getAttribute('data-args') || '["all"]')[0]; } catch (_e) { /* default */ }
    c.classList.toggle('on', arg === v);
  });
  renderLog();
}

// local day key / time from ISO ts (local timezone, not UTC slice)
const _dayKey = ts => { const d = new Date(ts); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };
const _hhmm = ts => { const d = new Date(ts); return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0'); };
const _dayLabel = (key, todayKey, ydayKey) => {
  if (key === todayKey) return 'TODAY · ' + key.slice(5);
  if (key === ydayKey) return 'YESTERDAY · ' + key.slice(5);
  return key;
};

// === RENDERING HISTORY LOG ===
/**
 * Render filtered history entries to #logB (table body)
 * Filters by search term (ticker + details) and action type (#logTF, chip-driven)
 */
export function renderLog() {
  const sEl = document.getElementById('logS');
  if (!sEl) return;
  const s = sEl.value.toLowerCase();
  const tf = document.getElementById('logTF').value;

  let it = S.history;
  if (tf !== 'all') it = it.filter(h => h.action.includes(tf));
  if (s) it = it.filter(h => h.ticker.toLowerCase().includes(s) || h.details.toLowerCase().includes(s));
  const hm = document.getElementById('histMeta');
  if (hm) hm.innerHTML = `<b>${S.history.length}</b> events logged · showing ${Math.min(it.length, 200)}`;

  // hero: today's count + type mix + last-event context (whole log, not the filtered view)
  const todayKey = _dayKey(now());
  const yd = new Date(); yd.setDate(yd.getDate() - 1);
  const ydayKey = _dayKey(yd.toISOString());
  const hn = document.getElementById('histHeroNum');
  if (hn) {
    const today = S.history.filter(h => _dayKey(h.ts) === todayKey);
    hn.textContent = today.length;
    const counts = {};
    today.forEach(h => { const t = _bdgType(h.action) || 'other'; counts[t] = (counts[t] || 0) + 1; });
    document.getElementById('histHeroChips').innerHTML = TYPES
      .filter(t => counts[t])
      .map(t => `<span class="neo-chip">${t.charAt(0).toUpperCase() + t.slice(1)}s <b>${counts[t]}</b></span>`)
      .join('') + (counts.other ? `<span class="neo-chip">Other <b>${counts.other}</b></span>` : '');
    const last = S.history[0];
    const ctx = document.getElementById('histHeroCtx');
    if (ctx) {
      if (!last) ctx.textContent = 'No activity yet — every trade, cash move and price refresh lands here.';
      else {
        const mins = Math.max(0, Math.round((Date.now() - new Date(last.ts)) / 60000));
        const rel = mins < 1 ? 'just now' : mins < 60 ? `${mins} min ago` : mins < 1440 ? `${Math.round(mins / 60)}h ago` : `${Math.round(mins / 1440)}d ago`;
        ctx.innerHTML = `Last event <b>${rel}</b> — ${esc(last.action)}${last.ticker ? ' · ' + esc(last.ticker) : ''}`;
      }
    }
  }

  // table with day separators (derived from ts, no data change)
  let lastDay = null;
  document.getElementById('logB').innerHTML = it.slice(0, 200).map(h => {
    const key = _dayKey(h.ts);
    const sep = key !== lastDay ? `<tr><td colspan="4" class="hist-day">${_dayLabel(key, todayKey, ydayKey)}</td></tr>` : '';
    lastDay = key;
    const ty = _bdgType(h.action);
    return `${sep}<tr><td class="hist-ts" title="${ds(h.ts)}">${_hhmm(h.ts)}</td><td><span class="hbdg ${ty}">${esc(h.action)}</span></td><td><strong>${esc(h.ticker)}</strong></td><td style="color:var(--t2)">${esc(h.details)}</td></tr>`;
  }).join('') || '<tr><td colspan="4" class="neo-empty">No history entries</td></tr>';
}
